const DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3';
const DRIVE_UPLOAD_BASE = 'https://www.googleapis.com/upload/drive/v3';

export interface ExportProgress {
  total: number;
  completed: number;
  current: string;
  failed: string[];
}

export interface ExportResult {
  success: boolean;
  fileId?: string;
  webViewLink?: string;
  error?: string;
}

export interface FolderResult {
  id: string;
  name: string;
  webViewLink?: string;
}

class GoogleDriveExportService {
  private accessToken: string | null = null;

  setAccessToken(token: string) {
    this.accessToken = token;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  private async makeRequest(url: string, options: RequestInit = {}): Promise<Response> {
    if (!this.accessToken) {
      throw new Error('No access token set. Please authenticate with Google Drive first.');
    }

    const headers = {
      'Authorization': `Bearer ${this.accessToken}`,
      ...options.headers,
    };

    return fetch(url, { ...options, headers });
  }

  async createFolder(name: string, parentId?: string): Promise<FolderResult> {
    const metadata: any = {
      name,
      mimeType: 'application/vnd.google-apps.folder',
    };

    if (parentId) {
      metadata.parents = [parentId];
    }

    const response = await this.makeRequest(`${DRIVE_API_BASE}/files?fields=id,name,webViewLink`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(metadata),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to create folder: ${error}`);
    }

    return await response.json();
  }

  async findOrCreateFolder(name: string, parentId?: string): Promise<FolderResult> {
    const query = parentId 
      ? `name='${name}' and mimeType='application/vnd.google-apps.folder' and '${parentId}' in parents and trashed=false`
      : `name='${name}' and mimeType='application/vnd.google-apps.folder' and 'root' in parents and trashed=false`;

    const response = await this.makeRequest(
      `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id,name,webViewLink)`
    );

    if (!response.ok) {
      throw new Error(`Failed to search for folder: ${await response.text()}`);
    }

    const data = await response.json();
    
    if (data.files && data.files.length > 0) {
      return data.files[0];
    }

    return await this.createFolder(name, parentId);
  }

  async uploadFile(
    content: string | Blob,
    filename: string,
    mimeType: string,
    parentId?: string
  ): Promise<ExportResult> {
    try {
      const metadata: any = {
        name: filename,
        mimeType,
      };

      if (parentId) {
        metadata.parents = [parentId];
      }

      const blob = typeof content === 'string' 
        ? new Blob([content], { type: mimeType })
        : content;

      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      form.append('file', blob);

      const response = await this.makeRequest(
        `${DRIVE_UPLOAD_BASE}/files?uploadType=multipart&fields=id,name,webViewLink`,
        {
          method: 'POST',
          body: form,
        }
      );

      if (!response.ok) {
        const error = await response.text();
        return { success: false, error: `Upload failed: ${error}` };
      }

      const result = await response.json();
      return {
        success: true,
        fileId: result.id,
        webViewLink: result.webViewLink,
      };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  async exportDocument(
    title: string,
    content: string,
    format: 'md' | 'txt' | 'html' = 'md',
    parentId?: string
  ): Promise<ExportResult> {
    const mimeTypes = {
      md: 'text/markdown',
      txt: 'text/plain',
      html: 'text/html',
    };

    const filename = `${title}.${format}`;
    return await this.uploadFile(content, filename, mimeTypes[format], parentId);
  }

  async exportChatThread(
    threadTitle: string,
    messages: Array<{ role: string; content: string; timestamp?: string }>,
    format: 'md' | 'txt' | 'html' = 'md',
    parentId?: string
  ): Promise<ExportResult> {
    let content: string;

    if (format === 'md') {
      content = `# ${threadTitle}\n\n`;
      content += `*Exported on ${new Date().toLocaleString()}*\n\n---\n\n`;
      
      for (const msg of messages) {
        const roleLabel = msg.role === 'user' ? '**You**' : '**AI**';
        const timestamp = msg.timestamp ? ` (${new Date(msg.timestamp).toLocaleString()})` : '';
        content += `${roleLabel}${timestamp}:\n\n${msg.content}\n\n---\n\n`;
      }
    } else if (format === 'html') {
      content = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${threadTitle}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; }
    .message { margin-bottom: 20px; padding: 15px; border-radius: 8px; }
    .user { background: #e3f2fd; }
    .assistant { background: #f5f5f5; }
    .role { font-weight: bold; margin-bottom: 8px; }
    .timestamp { color: #666; font-size: 12px; }
    hr { border: none; border-top: 1px solid #ddd; margin: 20px 0; }
  </style>
</head>
<body>
  <h1>${threadTitle}</h1>
  <p><em>Exported on ${new Date().toLocaleString()}</em></p>
  <hr>
`;
      for (const msg of messages) {
        const roleClass = msg.role === 'user' ? 'user' : 'assistant';
        const roleLabel = msg.role === 'user' ? 'You' : 'AI';
        const timestamp = msg.timestamp ? `<span class="timestamp">${new Date(msg.timestamp).toLocaleString()}</span>` : '';
        content += `  <div class="message ${roleClass}">
    <div class="role">${roleLabel} ${timestamp}</div>
    <div class="content">${msg.content.replace(/\n/g, '<br>')}</div>
  </div>\n`;
      }
      content += `</body>\n</html>`;
    } else {
      content = `${threadTitle}\n${'='.repeat(threadTitle.length)}\n\n`;
      content += `Exported on ${new Date().toLocaleString()}\n\n`;
      
      for (const msg of messages) {
        const roleLabel = msg.role === 'user' ? 'You' : 'AI';
        const timestamp = msg.timestamp ? ` (${new Date(msg.timestamp).toLocaleString()})` : '';
        content += `${roleLabel}${timestamp}:\n${msg.content}\n\n${'─'.repeat(40)}\n\n`;
      }
    }

    return await this.exportDocument(threadTitle, content, format, parentId);
  }

  async batchExportDocuments(
    documents: Array<{ title: string; content: string }>,
    folderName: string,
    format: 'md' | 'txt' | 'html' = 'md',
    onProgress?: (progress: ExportProgress) => void
  ): Promise<{ successful: ExportResult[]; failed: string[] }> {
    const folder = await this.findOrCreateFolder(folderName);
    
    const successful: ExportResult[] = [];
    const failed: string[] = [];

    for (let i = 0; i < documents.length; i++) {
      const doc = documents[i];
      
      if (onProgress) {
        onProgress({
          total: documents.length,
          completed: i,
          current: doc.title,
          failed,
        });
      }

      const result = await this.exportDocument(doc.title, doc.content, format, folder.id);
      
      if (result.success) {
        successful.push(result);
      } else {
        failed.push(doc.title);
      }
    }

    if (onProgress) {
      onProgress({
        total: documents.length,
        completed: documents.length,
        current: 'Complete!',
        failed,
      });
    }

    return { successful, failed };
  }

  async batchExportChatThreads(
    threads: Array<{ 
      title: string; 
      messages: Array<{ role: string; content: string; timestamp?: string }> 
    }>,
    folderName: string,
    format: 'md' | 'txt' | 'html' = 'md',
    onProgress?: (progress: ExportProgress) => void
  ): Promise<{ successful: ExportResult[]; failed: string[] }> {
    const folder = await this.findOrCreateFolder(folderName);
    
    const successful: ExportResult[] = [];
    const failed: string[] = [];

    for (let i = 0; i < threads.length; i++) {
      const thread = threads[i];
      
      if (onProgress) {
        onProgress({
          total: threads.length,
          completed: i,
          current: thread.title,
          failed,
        });
      }

      const result = await this.exportChatThread(thread.title, thread.messages, format, folder.id);
      
      if (result.success) {
        successful.push(result);
      } else {
        failed.push(thread.title);
      }
    }

    if (onProgress) {
      onProgress({
        total: threads.length,
        completed: threads.length,
        current: 'Complete!',
        failed,
      });
    }

    return { successful, failed };
  }
}

export const googleDriveExportService = new GoogleDriveExportService();
